import { BlueprintQuoteResponse } from '@monetize/types/app';
import { TDataTablePager } from '@monetize/types/common';
import {
  AppColumnDef,
  createColumnHelper,
} from '../../../../../components/Monetize/DataTableNew/appTable';
import React, { useMemo, useRef } from 'react';
import { MdLock } from 'react-icons/md';
import { handleApiErrorToast } from '../../../../../api/axios';
import {
  useDeleteBlueprintQuote,
  useUpdateBlueprintQuote,
} from '../../../../../api/cpqService';
import {
  MFlex,
  MIcon,
  MLink,
  MText,
  MTooltip,
} from '../../../../../components/Monetize';
import DataTableActions, {
  ActionType,
} from '../../../../../components/Monetize/DataTable/MDataTableActions';
import { DataTable } from '../../../../../components/Monetize/DataTableNew/DataTable';
import { DataTableRef } from '../../../../../components/Monetize/DataTableNew/DataTableTypes';
import {
  ACTION_COLUMN_KEY,
  createActionColumn,
} from '../../../../../components/Monetize/DataTableNew/dataTableNewRenderers';
import {
  getColHeaderWithSize,
  dateBodyTemplate as newDateBodyTemplate,
  textBodyTemplate as newTextBodyTemplate,
} from '../../../../../components/Monetize/DataTableNew/tableUtilsNew';
import { BLUEPRINT_QUOTE_SCOPE_DISPLAY } from '../../../../../constants/quotes';
import { getQuoteBaseRoute } from '../../../../../constants/routes';
import { BLUEPRINT_ADMIN_ROLES } from '../../../../../services/acl/roles';
import { useAuth } from '../../../../../services/auth0';
import { useConfirmModal } from '../../../../../services/confirmModal';
import { useToast } from '../../../../../services/toast';

const columnHelper = createColumnHelper<BlueprintQuoteResponse>();

const getColumns = (
  actionBodyTemplate: (rowData: BlueprintQuoteResponse) => React.ReactNode,
): AppColumnDef<BlueprintQuoteResponse, any>[] => [
  columnHelper.display({
    id: 'name',
    ...getColHeaderWithSize('Name', 200),
    cell: ({ row }) => {
      const data = row.original as BlueprintQuoteResponse;
      return (
        <MTooltip
          label="This blueprint is being used in a quote and cannot be deleted."
          isDisabled={!data.isLocked}
        >
          <MFlex alignItems="center" position="relative">
            <MText lineClamp={3}>{data.name || '-'}</MText>
            {data.isLocked && <MIcon as={MdLock} color="tPurple.base" ml="1" />}
          </MFlex>
        </MTooltip>
      );
    },
    enableSorting: true,
  }),
  columnHelper.accessor('description', {
    ...getColHeaderWithSize('Description', 250),
    cell: newTextBodyTemplate(),
    enableSorting: true,
  }),
  columnHelper.display({
    id: 'quote',
    ...getColHeaderWithSize('Quote', 200),
    cell: ({ row }) => {
      const data = row.original as BlueprintQuoteResponse;
      return (
        <MFlex flexDir="column">
          <MText lineClamp={1}>{data.quote?.description || '-'}</MText>
          {data.quote?.id && (
            <MLink
              href={getQuoteBaseRoute(data.quote.id)}
              fontSize="xs"
              color="tPurple.base"
              onClick={(ev) => ev.stopPropagation()}
            >
              {data.quote.id}
            </MLink>
          )}
        </MFlex>
      );
    },
    enableSorting: false,
  }),
  columnHelper.accessor('quoteCurrency', {
    ...getColHeaderWithSize('Currency', 120),
    cell: newTextBodyTemplate(),
    enableSorting: true,
  }),
  columnHelper.display({
    id: 'isActive',
    ...getColHeaderWithSize('Active', 100),
    cell: ({ row }) => {
      const data = row.original as BlueprintQuoteResponse;
      return <MText>{data.isActive ? 'Yes' : 'No'}</MText>;
    },
    enableSorting: false,
  }),
  columnHelper.display({
    id: 'scope',
    ...getColHeaderWithSize('Available For', 170),
    cell: ({ row }) => {
      const data = row.original as BlueprintQuoteResponse;
      return <MText>{BLUEPRINT_QUOTE_SCOPE_DISPLAY[data.scope]}</MText>;
    },
    enableSorting: false,
  }),
  columnHelper.accessor('createDate', {
    ...getColHeaderWithSize('Created', 170),
    cell: newDateBodyTemplate(),
    enableSorting: true,
  }),
  columnHelper.accessor('modifyDate', {
    ...getColHeaderWithSize('Updated', 170),
    cell: newDateBodyTemplate(),
    enableSorting: true,
  }),
  createActionColumn<BlueprintQuoteResponse>(actionBodyTemplate),
];

interface BlueprintTableProps {
  data: BlueprintQuoteResponse[];
  isLoading: boolean;
  onEdit: (blueprintId: string) => void;
  totalElements?: number;
  pager: TDataTablePager;
  setPager: (pager: TDataTablePager) => void;
}

export const BlueprintTable = ({
  data,
  isLoading,
  onEdit,
  totalElements = 0,
  pager,
  setPager,
}: BlueprintTableProps) => {
  const { addToast } = useToast();
  const { showConfirmModal, setModalLoading, closeModal } = useConfirmModal();
  const { currentTenantUser, currentTenantUserHasRole } = useAuth();
  const isAdmin = currentTenantUserHasRole(BLUEPRINT_ADMIN_ROLES);
  const tableRef = useRef<DataTableRef>(null);

  const { mutate: updateBlueprint, isPending: isUpdatingStatus } =
    useUpdateBlueprintQuote({
      onSuccess: () => {
        addToast({
          severity: 'success',
          summary: 'Blueprint status updated.',
        });
      },
      onError: (err) => {
        handleApiErrorToast(err);
      },
    });

  const { mutate: deleteBlueprint } = useDeleteBlueprintQuote({
    onSuccess: () => {
      closeModal();
    },
    onError: (err) => {
      handleApiErrorToast(err);
      setModalLoading(false);
    },
  });

  const handleDelete = (blueprint: BlueprintQuoteResponse) => {
    const onYes = async () => {
      setModalLoading(true);
      deleteBlueprint({ blueprintId: blueprint.id });
    };

    showConfirmModal({
      title: 'Delete Blueprint',
      description:
        'Are you sure you want to delete this blueprint? This action cannot be undone. Deleting this blueprint will not delete the associated quote.',
      onYes,
      yesBtnProps: {
        variant: 'delete',
      },
      noBtnProps: {
        variant: 'cancel',
      },
      yesButton: 'Delete',
      noButton: 'Cancel',
    });
  };

  const handleToggleActive = (blueprint: BlueprintQuoteResponse) => {
    updateBlueprint({
      blueprintId: blueprint.id,
      payload: {
        name: blueprint.name,
        description: blueprint.description ?? null,
        isActive: !blueprint.isActive,
      },
    });
  };

  const actionBodyTemplate = (rowData: BlueprintQuoteResponse) => {
    const { isLocked } = rowData;
    const { id, name, isActive } = rowData;
    const deactivateConfirmProps = {
      title: `Are you sure you want to deactivate "${name}"?`,
      description:
        'Once deactivated, this blueprint will no longer be available.',
      yesButton: 'Deactivate',
    };

    const actions: ActionType[] = [
      {
        title: 'Edit',
        action: () => onEdit(rowData.id),
        enabled: true,
      },
      {
        title: 'Activate',
        enabled: !isActive && !isUpdatingStatus,
        action: () => handleToggleActive(rowData),
      },
      {
        title: 'Deactivate',
        color: 'danger',
        confirmProps: deactivateConfirmProps,
        enabled: isActive && !isUpdatingStatus,
        action: () => handleToggleActive(rowData),
      },
      {
        title: 'Delete',
        color: 'danger',
        enabled: !isLocked,
        action: () => handleDelete(rowData),
      },
    ];

    return <DataTableActions actions={actions} />;
  };

  const columns = useMemo(
    () => getColumns(actionBodyTemplate),
    [actionBodyTemplate],
  );

  return (
    <DataTable
      ref={tableRef}
      columns={columns}
      data={data}
      isLoading={isLoading}
      enablePagination
      pinnedRightColumns={[ACTION_COLUMN_KEY]}
      totalElements={totalElements}
      pager={pager}
      setPager={setPager}
      onRowClick={(row) => onEdit(row.id)}
      emptyProps={{
        mainMessage: 'No quote blueprints found.',
        smallMessage:
          'Create a blueprint from an existing quote to get started.',
      }}
    />
  );
};
